import React, { useContext, useEffect, useState, useRef } from 'react';
import {
    Form,
    Input,
    Modal,
    Select,
    Typography,
    Divider,
    Space,
    Tag,
    Row,
    Col,
    Button,
    Tooltip,
    Upload,
    Image,
    message,
    InputNumber,
    Card
} from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { AuthContext } from '../../../../config/AuthContext';
import BbddColors from '../../../../styles/BbddColors';
import { PostBbddActivitatIdProblema } from '../../../../dao/bbdd/PostBbddActivitatIdProblema';
import { GetAulaDatabases, AulaDatabase } from '../../../../dao/bbdd/AulaDatabasesApi';
import { GetAules } from '../../../../dao/GetAules';
import { GetPractica } from '../../../../dao/bbdd/GetPracticaId';
import {
    DatabaseOutlined,
    CodeOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ApartmentOutlined,
    DownloadOutlined,
    UploadOutlined,
    FullscreenOutlined,
    SettingOutlined,
    QuestionCircleOutlined,
    PictureOutlined,
    TagsOutlined,
    PlusOutlined,
    DeleteOutlined,
    EyeOutlined,
    EditOutlined
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import ErDiagramIde from '../../disseny/ErDiagramIde';
import ScriptRelationalIde from '../../disseny/ScriptRelationalIde';
import RelationalDiagramIde from '../../disseny/RelationalDiagramIde';
import { ErDiagramData } from '../../disseny/model/ErModel';
import { DesignTestsConfig } from '../../disseny/model/DesignTestConfig';
import ConfiguracioTestsDissenyModal from './ConfiguracioTestsDissenyModal';
import ConfiguracioTestsSqlModal from './ConfiguracioTestsSqlModal';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export const PREDEFINED_BBDD_TAGS = [
    'SELECT',
    'JOIN',
    'LEFT JOIN',
    'GROUP BY',
    'HAVING',
    'ORDER BY',
    'SUBQUERIES',
    'INSERT',
    'UPDATE',
    'DELETE',
    'DML',
    'DDL',
    'CREATE TABLE',
    'ALTER TABLE',
    'CONSTRAINTS',
    'FOREIGN KEY',
    'TRIGGER',
    'PROCEDURE',
    'FUNCTION',
    'VIEW',
    'INDEX',
    'TRANSACTIONS',
    'ER-CONCEPTUAL',
    'ER-EXTENDED',
    'RELACIONAL',
    'NORMALIZATION',
    'NoSQL',
    'MONGODB',
    'EXAMEN',
    'PRÀCTICA',
    'AVANÇAT'
];

export const GRUPS_TIPUS_EXERCICI_BBDD = [
    {
        group: 'Disseny (Modelatge)',
        options: [
            { label: 'ER (Entitat-Relació)', value: 'ER' },
            { label: 'Script (Relacional)', value: 'Script' },
            { label: 'Relacional', value: 'Relacional' }
        ]
    },
    {
        group: 'SQL (Consultes i Manipulació DML)',
        options: [
            { label: 'SQL: SELECT (Consultes de dades)', value: 'SQL:SELECT' },
            { label: 'SQL: INSERT (Inserció de registres)', value: 'SQL:INSERT' },
            { label: 'SQL: UPDATE (Modificació de registres)', value: 'SQL:UPDATE' },
            { label: 'SQL: DELETE (Eliminació de registres)', value: 'SQL:DELETE' },
            { label: 'SQL General', value: 'SQL' }
        ]
    },
    {
        group: 'PL/SQL (Rutines i Objectes)',
        options: [
            { label: 'PLSQL: PROCEDURE (Procediment)', value: 'PLSQL:PROCEDURE' },
            { label: 'PLSQL: FUNCTION (Funció)', value: 'PLSQL:FUNCTION' },
            { label: 'PLSQL: TRIGGER (Disparador)', value: 'PLSQL:TRIGGER' },
            { label: 'PLSQL: VIEW (Vista)', value: 'PLSQL:VIEW' },
            { label: 'PLSQL General', value: 'PLSQL' }
        ]
    },
    {
        group: 'DDL & Administració',
        options: [
            { label: 'DDL (Create, Alter, Drop, Truncate)', value: 'DDL' },
            { label: 'DCL (Grant, Revoke)', value: 'DCL' },
            { label: 'NoSQL (MongoDB / Documents JSON)', value: 'NoSQL' }
        ]
    }
];

export const TIPUS_EXERCICI_BBDD = GRUPS_TIPUS_EXERCICI_BBDD.flatMap(g => g.options);

interface Props {
    id_activitat?: string;
    id_aula?: string | number;
    element: any;
    visible: boolean;
    onClose: (value: boolean) => void;
    title?: string;
}

const NouExerciciBbddModal: React.FC<Props> = (props: Props) => {
    const [form] = Form.useForm();
    const { isLoggedIn, userId } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [aulaDatabases, setAulaDatabases] = useState<AulaDatabase[]>([]);
    const [loadingAulaDbs, setLoadingAulaDbs] = useState<boolean>(false);

    useEffect(() => {
        const loadAulaDbs = async () => {
            if (!props.visible) return;
            setLoadingAulaDbs(true);
            try {
                let targetAulaId = props.id_aula;
                if (!targetAulaId && props.id_activitat) {
                    try {
                        const act = await GetPractica(props.id_activitat);
                        if (act?.idAula || act?.id_aula) {
                            targetAulaId = act.idAula || act.id_aula;
                        }
                    } catch (_) {}
                }
                if (targetAulaId) {
                    const dbs = await GetAulaDatabases(targetAulaId);
                    setAulaDatabases(dbs || []);
                } else if (userId) {
                    const aules = await GetAules(String(userId));
                    const allDbs: AulaDatabase[] = [];
                    for (const a of (aules || [])) {
                        const aId = a.id || a.key;
                        if (aId) {
                            try {
                                const dbs = await GetAulaDatabases(aId);
                                if (dbs && dbs.length > 0) {
                                    allDbs.push(...dbs);
                                }
                            } catch (_) {}
                        }
                    }
                    setAulaDatabases(allDbs);
                }
            } catch (err) {
                console.warn('Error carregant bases de dades d aula:', err);
            } finally {
                setLoadingAulaDbs(false);
            }
        };
        loadAulaDbs();
    }, [props.visible, props.id_aula, props.id_activitat]);
    const [selectedTipus, setSelectedTipus] = useState<string>('SQL');
    const [erDesignerVisible, setErDesignerVisible] = useState(false);
    const [scriptDesignerVisible, setScriptDesignerVisible] = useState(false);
    const [relationalDesignerVisible, setRelationalDesignerVisible] = useState(false);
    const [configTestsVisible, setConfigTestsVisible] = useState(false);
    const [configSqlTestsVisible, setConfigSqlTestsVisible] = useState(false);
    const [penalitzacioIntent, setPenalitzacioIntent] = useState<number>(0);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [previewImage, setPreviewImage] = useState<string>('');
    const [previewOpen, setPreviewOpen] = useState<boolean>(false);
    const jsonFileInputRef = useRef<HTMLInputElement | null>(null);

    // Suport per vincular un diagrama/model de suport a l'enunciat (ER, Relacional o Script)
    const [enunciatDiagram, setEnunciatDiagram] = useState<{
        type: 'ER' | 'Script' | 'Relacional';
        data: any;
    } | null>(null);
    const [enunciatDesignerVisible, setEnunciatDesignerVisible] = useState(false);
    const [enunciatDesignerType, setEnunciatDesignerType] = useState<'ER' | 'Script' | 'Relacional'>('ER');

    useEffect(() => {
        if (props.visible) {
            if (props.element && props.element.id) {
                const initialTipus = props.element.tipus || 'SQL';
                setSelectedTipus(initialTipus);

                // Extreure enunciatDiagram si estava desat a checks
                let loadedEnunciatDiagram: { type: 'ER' | 'Script' | 'Relacional'; data: any } | null = null;
                const rawChecks = props.element.checks || '';
                if (rawChecks.includes('ENUNCIAT_DIAGRAM#')) {
                    try {
                        const parts = rawChecks.split('ENUNCIAT_DIAGRAM#');
                        if (parts[1]) {
                            const endIdx = parts[1].indexOf('##');
                            const jsonStr = endIdx !== -1 ? parts[1].slice(0, endIdx) : parts[1];
                            loadedEnunciatDiagram = JSON.parse(jsonStr);
                        }
                    } catch (e) {
                        console.warn('Error parsejant ENUNCIAT_DIAGRAM des de checks:', e);
                    }
                }
                setEnunciatDiagram(loadedEnunciatDiagram);

                // Extreure penalització si estava configurada al checks
                let initialPenalty = 0;
                // Intentar extreure-la de JSON si hi és
                const extractPenaltyFromJson = (str: string): number | null => {
                    if (!str || !str.includes('{')) return null;
                    try {
                        // Buscar el primer { i l'últim } d'un bloc de rúbrica
                        const start = str.indexOf('{');
                        const end = str.lastIndexOf('}');
                        if (start !== -1 && end !== -1 && end > start) {
                            const candidate = str.substring(start, end + 1);
                            const parsed = JSON.parse(candidate);
                            if (parsed && parsed.penalitzacioPerIntent !== undefined && parsed.penalitzacioPerIntent !== null) {
                                return Number(parsed.penalitzacioPerIntent) || 0;
                            }
                        }
                    } catch (e) {
                        // Si falla perquè hi ha altres blocs com ENUNCIAT_DIAGRAM, provar de dividir per tokens o regex
                        const match = str.match(/"penalitzacioPerIntent"\s*:\s*([0-9.]+)/);
                        if (match && match[1]) {
                            return Number(match[1]) || 0;
                        }
                    }
                    return null;
                };

                const penaltyFromJson = extractPenaltyFromJson(rawChecks);
                if (penaltyFromJson !== null) {
                    initialPenalty = penaltyFromJson;
                } else if (rawChecks.includes('PENALTY:')) {
                    const match = rawChecks.match(/PENALTY:([0-9.]+)/);
                    if (match && match[1]) initialPenalty = Number(match[1]) || 0;
                }
                setPenalitzacioIntent(initialPenalty);

                // Parsejar tags si venen informats
                let parsedTags: string[] = [];
                if (props.element.tags) {
                    if (Array.isArray(props.element.tags)) {
                        parsedTags = props.element.tags;
                    } else if (typeof props.element.tags === 'string') {
                        parsedTags = props.element.tags.split(/[\s,]+/).filter(Boolean);
                    }
                }

                const normalizeImgUrl = (item: string) => {
                    let s = (item || '').trim();
                    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
                        s = s.slice(1, -1).trim();
                    }
                    if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')) {
                        return s;
                    }
                    try {
                        if (s.startsWith('ZGF0YTppbWFnZS')) {
                            const dec = window.atob(s);
                            if (dec.startsWith('data:image')) return dec;
                        }
                    } catch (e) {}
                    return s.startsWith('/9j/') ? `data:image/jpeg;base64,${s}` : `data:image/png;base64,${s}`;
                };

                // Carregar imatges si venen informades (suport multi-imatge o imatge única)
                const existingFiles: UploadFile[] = [];
                if (props.element.img) {
                    const rawImg = typeof props.element.img === 'string' ? props.element.img : String(props.element.img);
                    if (rawImg.startsWith('[')) {
                        try {
                            const arr = JSON.parse(rawImg);
                            if (Array.isArray(arr)) {
                                arr.forEach((item: string, idx: number) => {
                                    const url = normalizeImgUrl(item);
                                    existingFiles.push({
                                        uid: `img-${idx}`,
                                        name: `imatge-${idx + 1}.png`,
                                        status: 'done',
                                        url: url,
                                        thumbUrl: url
                                    });
                                });
                            }
                        } catch (e) {
                            const url = normalizeImgUrl(rawImg);
                            existingFiles.push({
                                uid: 'img-0',
                                name: 'imatge-1.png',
                                status: 'done',
                                url: url,
                                thumbUrl: url
                            });
                        }
                    } else if (rawImg.trim().length > 0) {
                        const url = normalizeImgUrl(rawImg);
                        existingFiles.push({
                            uid: 'img-0',
                            name: 'imatge-1.png',
                            status: 'done',
                            url: url,
                            thumbUrl: url
                        });
                    }
                }
                setFileList(existingFiles);

                form.setFieldsValue({
                    id: props.element.id,
                    nom: props.element.nom || props.element.prac,
                    descripcio: props.element.descripcio || props.element.enunciat,
                    enunciat: props.element.enunciat || props.element.descripcio,
                    dificultat: props.element.dificultat || 'beginner',
                    tipus: initialTipus,
                    tags: parsedTags,
                    id_aula_database: props.element.id_aula_database ? Number(props.element.id_aula_database) : (props.element.idAulaDatabase ? Number(props.element.idAulaDatabase) : undefined),
                    correccio: props.element.correccio || '',
                    checks: props.element.checks || ''
                });
            } else {
                form.resetFields();
                setSelectedTipus('SQL');
                setPenalitzacioIntent(0);
                setFileList([]);
                setEnunciatDiagram(null);
                form.setFieldsValue({
                    id: 0,
                    dificultat: 'beginner',
                    tipus: 'SQL',
                    tags: []
                });
            }
        }
    }, [props.element, props.visible]);

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        setEnunciatDiagram(null);
        props.onClose(false);
    };

    const handleExportCorreccioJson = () => {
        const val = form.getFieldValue('correccio');
        if (!val || !val.trim()) {
            message.warning("No hi ha cap contingut de solució per exportar.");
            return;
        }
        try {
            const parsed = JSON.parse(val);
            const blob = new Blob([JSON.stringify(parsed, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `solucio_er_${form.getFieldValue('nom') || 'exercici'}.json`;
            a.click();
            URL.revokeObjectURL(url);
            message.success("Solució JSON exportada correctament.");
        } catch (e) {
            // Si no és un JSON vàlid encara, exportem com a text pla
            const blob = new Blob([val], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `solucio_${form.getFieldValue('nom') || 'exercici'}.json`;
            a.click();
            URL.revokeObjectURL(url);
        }
    };

    const handleImportCorreccioJson = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const text = evt.target?.result as string;
                // Validar que sigui json parsejable
                JSON.parse(text);
                form.setFieldsValue({ correccio: text });
                message.success("Solució JSON importada correctament al formulari.");
            } catch (err) {
                message.error("El fitxer seleccionat no és un JSON vàlid.");
            }
        };
        reader.readAsText(file);
        if (jsonFileInputRef.current) jsonFileInputRef.current.value = '';
    };

    const handleUploadChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
        const processedList: UploadFile[] = [];
        for (const file of newFileList) {
            if (file.originFileObj && !file.url) {
                const base64 = await fileToBase64(file.originFileObj);
                processedList.push({
                    ...file,
                    status: 'done',
                    url: base64,
                    thumbUrl: base64
                });
            } else {
                processedList.push(file);
            }
        }
        setFileList(processedList);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handlePreview = async (file: UploadFile) => {
        setPreviewImage(file.url || file.thumbUrl || '');
        setPreviewOpen(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Auto-enrich checks depending on subtype
            let checks = form.getFieldValue('checks') || '';
            const t = values.tipus || 'SQL';
            const isDesign = t === 'ER' || t === 'Script' || t === 'Relacional';

            if (isDesign) {
                // Si el checks ja té un JSON de rúbrica, assegurem que tingui la penalització
                if (checks.includes('{') && checks.includes('"items"')) {
                    try {
                        const hashIdx = checks.indexOf('#');
                        const prefix = hashIdx !== -1 ? checks.slice(0, hashIdx + 1) : '';
                        const jsonStr = hashIdx !== -1 ? checks.slice(hashIdx + 1) : checks;
                        const parsed = JSON.parse(jsonStr);
                        parsed.penalitzacioPerIntent = Number(penalitzacioIntent) || 0;
                        checks = prefix + JSON.stringify(parsed);
                    } catch (e) {
                        // En cas que checks contingui altres tokens, reemplacem per regex
                        checks = checks.replace(/"penalitzacioPerIntent"\s*:\s*[0-9.]+/, `"penalitzacioPerIntent":${Number(penalitzacioIntent) || 0}`);
                    }
                } else {
                    const prefix = t === 'ER' ? 'ER-VALIDATION' : (t === 'Script' ? 'SCRIPT-VALIDATION' : 'RELATIONAL-VALIDATION');
                    checks = `${prefix}#{"version":1,"penalitzacioPerIntent":${Number(penalitzacioIntent) || 0},"items":[]}`;
                }
            } else {
                // Per exercicis SQL, PLSQL, DDL, NoSQL
                if (t === 'SQL:INSERT' && !checks.includes('DML-')) checks = checks ? `DML-INSERT#${checks}` : 'DML-INSERT';
                else if (t === 'SQL:UPDATE' && !checks.includes('DML-')) checks = checks ? `DML-UPDATE#${checks}` : 'DML-UPDATE';
                else if (t === 'SQL:DELETE' && !checks.includes('DML-')) checks = checks ? `DML-DELETE#${checks}` : 'DML-DELETE';
                else if (t === 'PLSQL:VIEW' && !checks.includes('VISTA-')) checks = checks ? `VISTA-#${checks}` : 'VISTA-';
                else if (t === 'DDL' && !checks.includes('DDL-')) checks = checks ? `DDL-#${checks}` : 'DDL-';
                else if (t === 'NoSQL' && !checks.includes('NOSQL-')) checks = checks ? `NOSQL-#${checks}` : 'NOSQL-';

                // Gestionar la penalització en SQL (PENALTY:X o dins del JSON #SQL-TESTS#)
                if (checks.includes('#SQL-TESTS#')) {
                    try {
                        const parts = checks.split('#SQL-TESTS#');
                        const prefix = parts[0];
                        const sqlConfig = JSON.parse(parts[1]);
                        sqlConfig.penalitzacioPerIntent = Number(penalitzacioIntent) || 0;
                        checks = `${prefix}#SQL-TESTS#${JSON.stringify(sqlConfig)}`;
                    } catch (e) {
                        checks = checks.replace(/"penalitzacioPerIntent"\s*:\s*[0-9.]+/, `"penalitzacioPerIntent":${Number(penalitzacioIntent) || 0}`);
                    }
                } else {
                    // Actualitzar o afegir PENALTY:X
                    if (checks.includes('PENALTY:')) {
                        checks = checks.replace(/PENALTY:[0-9.]+/, `PENALTY:${penalitzacioIntent}`);
                    } else if (penalitzacioIntent > 0) {
                        checks = checks ? `${checks}#PENALTY:${penalitzacioIntent}` : `PENALTY:${penalitzacioIntent}`;
                    }
                }
            }

            // Si s'ha vinculat un diagrama/model a l'enunciat, persistir-lo a checks
            if (enunciatDiagram) {
                // Netejar qualsevol ENUNCIAT_DIAGRAM previ
                if (checks.includes('ENUNCIAT_DIAGRAM#')) {
                    checks = checks.replace(/ENUNCIAT_DIAGRAM#.*?##/, '').trim();
                }
                const diagSerialized = JSON.stringify(enunciatDiagram);
                checks = checks ? `${checks}#ENUNCIAT_DIAGRAM#${diagSerialized}##` : `ENUNCIAT_DIAGRAM#${diagSerialized}##`;
            } else if (checks.includes('ENUNCIAT_DIAGRAM#')) {
                // Si s'ha desvinculat, eliminar-lo
                checks = checks.replace(/ENUNCIAT_DIAGRAM#.*?##/, '').trim();
            }

            // Serialitzar les imatges pujades a base64 (suport de múltiples imatges)
            let imgPayload: string = '';
            if (fileList.length > 0) {
                const imagesArray = fileList
                    .map(f => f.url || f.thumbUrl || '')
                    .filter(Boolean);
                if (imagesArray.length === 1) {
                    imgPayload = imagesArray[0];
                } else if (imagesArray.length > 1) {
                    imgPayload = JSON.stringify(imagesArray);
                }
            }

            // Normalitzar tags
            const tagsStr = Array.isArray(values.tags) ? values.tags.join(' ') : (values.tags || '');

            const payload = {
                id: values.id || 0,
                nom: values.nom,
                descripcio: values.descripcio || '',
                enunciat: values.enunciat || values.descripcio || '',
                dificultat: values.dificultat || 'beginner',
                tipus: values.tipus || 'SQL',
                correccio: values.correccio || '',
                checks: checks,
                tags: tagsStr,
                img: imgPayload,
                id_aula_database: values.id_aula_database ? Number(values.id_aula_database) : null
            };

            await PostBbddActivitatIdProblema(props.id_activitat || '0', payload);
            setLoading(false);
            form.resetFields();
            setFileList([]);
            props.onClose(true);
        } catch (error) {
            console.error('Error al guardar l\'exercici:', error);
            setLoading(false);
        }
    };

    const isEr = selectedTipus === 'ER';
    const isScript = selectedTipus === 'Script';
    const isRelacional = selectedTipus === 'Relacional';
    const isDesignExercise = isEr || isScript || isRelacional;
    const isNoSql = selectedTipus === 'NoSQL';

    const codeEditorLanguage = isNoSql ? 'javascript' : 'sql';

    return (
        <>
            <Modal
                title={
                    <Space>
                        {isDesignExercise ? (
                            <ApartmentOutlined style={{ color: '#722ed1', fontSize: 22 }} />
                        ) : (
                            <DatabaseOutlined style={{ color: '#1890ff', fontSize: 22 }} />
                        )}
                        <span style={{ fontWeight: 700, fontSize: 16 }}>
                            {props.element?.id ? "Editar Exercici de Base de Dades" : "Nou Exercici de Base de Dades"}
                        </span>
                        {isDesignExercise && (
                            <Tag color="purple" style={{ fontWeight: 600 }}>Disseny de Dades ({selectedTipus})</Tag>
                        )}
                    </Space>
                }
                open={props.visible}
                onCancel={handleCancel}
                width={880}
                style={{ top: 20 }}
                destroyOnClose
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        Cancel·lar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={handleSubmit}
                        style={{
                            background: isDesignExercise ? '#722ed1' : '#1890ff',
                            borderColor: isDesignExercise ? '#722ed1' : '#1890ff',
                            fontWeight: 600,
                            paddingLeft: 24,
                            paddingRight: 24
                        }}
                    >
                        {props.element?.id ? "Desar Canvis" : "Crear Exercici"}
                    </Button>
                ]}
            >
                <Form form={form} layout="vertical" initialValues={{ dificultat: 'beginner', tipus: 'SQL', tags: [] }}>
                    <Form.Item name="id" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="checks" hidden>
                        <Input />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={14}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Nom / Títol de l'exercici</span>}
                                name="nom"
                                rules={[{ required: true, message: "Si us plau, introdueix el nom de l'exercici" }]}
                            >
                                <Input placeholder="Ex: Disseny ER de Gestió d'Estadis i Equips" />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Tipus d'exercici</span>}
                                name="tipus"
                                rules={[{ required: true, message: "Selecciona el tipus d'exercici" }]}
                            >
                                <Select
                                    placeholder="Selecciona el tipus"
                                    onChange={(val) => {
                                        setSelectedTipus(val);
                                    }}
                                >
                                    {GRUPS_TIPUS_EXERCICI_BBDD.map(g => (
                                        <Select.OptGroup key={g.group} label={g.group}>
                                            {g.options.map(t => (
                                                <Option key={t.value} value={t.value}>
                                                    <Tag color={t.value.startsWith('ER') || t.value === 'Script' || t.value === 'Relacional' ? 'purple' : 'geekblue'} style={{ marginRight: 6 }}>
                                                        {t.value}
                                                    </Tag>
                                                    {t.label}
                                                </Option>
                                            ))}
                                        </Select.OptGroup>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={
                                    <Space size={4}>
                                        <DatabaseOutlined style={{ color: '#1890ff' }} />
                                        <span style={{ fontWeight: 600 }}>Base de Dades d'Aula per a la Consulta / Avaluació</span>
                                        <Tooltip title="Selecciona sobre quina base de dades d'aula s'ha d'executar i comparar la solució (només lectura per a alumnes).">
                                            <QuestionCircleOutlined style={{ color: '#888' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                name="id_aula_database"
                            >
                                <Select
                                    placeholder="Cap base de dades associada (esquema buit o per defecte)"
                                    allowClear
                                    loading={loadingAulaDbs}
                                    options={aulaDatabases.map(db => ({
                                        value: db.id,
                                        label: `${db.nom} (${db.schema_name})`
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={14}>
                            <Form.Item
                                label={
                                    <Space size={4}>
                                        <TagsOutlined style={{ color: '#1890ff' }} />
                                        <span style={{ fontWeight: 600 }}>Tags / Tipologia de l'exercici</span>
                                        <Tooltip title="Afegeix paraules clau o categories del problema (ex: SELECT, JOIN, DDL, SUBQUERIES, EXAMEN...)">
                                            <QuestionCircleOutlined style={{ color: '#888' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                name="tags"
                            >
                                <Select
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    placeholder="Escriu o selecciona etiquetes (ex: SELECT, JOIN, ER...)"
                                    tokenSeparators={[',', ' ']}
                                    options={PREDEFINED_BBDD_TAGS.map(tag => ({ value: tag, label: tag }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={isDesignExercise ? 5 : 5}>
                            <Form.Item label={<span style={{ fontWeight: 600 }}>Dificultat</span>} name="dificultat" rules={[{ required: true }]}>
                                <Select>
                                    <Option value="beginner"><Tag color="green">Fàcil</Tag></Option>
                                    <Option value="intermediate"><Tag color="orange">Mitjà</Tag></Option>
                                    <Option value="expert"><Tag color="red">Avançat</Tag></Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={isDesignExercise ? 5 : 5}>
                            <Form.Item
                                label={
                                    <Space size={4}>
                                        <span style={{ fontWeight: 600 }}>Penalització</span>
                                        <Tooltip title="Punts (0-100) que es resten per intent fallit.">
                                            <QuestionCircleOutlined style={{ color: '#888' }} />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <InputNumber
                                    min={0}
                                    max={50}
                                    value={penalitzacioIntent}
                                    onChange={(v) => setPenalitzacioIntent(v !== null ? v : 0)}
                                    placeholder="0"
                                    addonAfter="pts"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        {isDesignExercise ? (
                            <Col span={24} style={{ marginBottom: 12 }}>
                                <Card size="small" style={{ background: '#f9f0ff', borderColor: '#d3adf7' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Space>
                                            <SettingOutlined style={{ color: '#722ed1', fontSize: 16 }} />
                                            <div>
                                                <Text strong style={{ color: '#722ed1' }}>Rúbrica de Tests i Validació Automàtica (100 punts)</Text>
                                                <div style={{ fontSize: 12, color: '#666' }}>
                                                    Configura la ponderació exacta de cadascuna de les entitats, taules, relacions i atributs de la solució.
                                                </div>
                                            </div>
                                        </Space>
                                        <Button
                                            type="primary"
                                            icon={<SettingOutlined />}
                                            onClick={() => setConfigTestsVisible(true)}
                                            style={{ background: '#722ed1', borderColor: '#722ed1', fontWeight: 600 }}
                                        >
                                            Configurar Tests
                                        </Button>
                                    </div>
                                </Card>
                            </Col>
                        ) : (
                            <Col span={24} style={{ marginBottom: 12 }}>
                                <Card size="small" style={{ background: '#e6fffb', borderColor: '#87e8de' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Space>
                                            <SettingOutlined style={{ color: '#13c2c2', fontSize: 16 }} />
                                            <div>
                                                <Text strong style={{ color: '#08979c' }}>Configuració de Tests i Comprovacions SQL</Text>
                                                <div style={{ fontSize: 12, color: '#666' }}>
                                                    Configura ordenació estricta, clàusules obligatòries o prohibides (WHERE, JOIN, GROUP BY...) i penalització per intents fallits.
                                                </div>
                                            </div>
                                        </Space>
                                        <Button
                                            type="primary"
                                            icon={<SettingOutlined />}
                                            onClick={() => setConfigSqlTestsVisible(true)}
                                            style={{ background: '#13c2c2', borderColor: '#13c2c2', fontWeight: 600 }}
                                        >
                                            Configurar Tests SQL
                                        </Button>
                                    </div>
                                </Card>
                            </Col>
                        )}
                    </Row>

                    {/* Apartat de Vincular Diagrama/Model a l'Enunciat */}
                    <div style={{ marginBottom: 18 }}>
                        <Card
                            size="small"
                            style={{
                                background: enunciatDiagram ? '#f6ffed' : '#fafafa',
                                borderColor: enunciatDiagram ? '#b7eb8f' : '#d9d9d9',
                                borderRadius: 8
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                <Space direction="vertical" size={2}>
                                    <Space size={6}>
                                        <ApartmentOutlined style={{ color: enunciatDiagram ? '#52c41a' : '#722ed1', fontSize: 16 }} />
                                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                                            Model / Diagrama vinculat a l'Enunciat
                                        </span>
                                        <Tooltip title="Permet adjuntar un Diagrama ER, un Model Relacional o un Script Relacional com a part activa de l'enunciat. L'alumne el veurà dibuixat o formulat en la pestanya 'Enunciat' i podrà respondre preguntes o consultes basant-se en aquest model.">
                                            <QuestionCircleOutlined style={{ color: '#888' }} />
                                        </Tooltip>
                                    </Space>
                                    <div style={{ fontSize: 12, color: '#666' }}>
                                        {enunciatDiagram ? (
                                            <span>
                                                S'ha adjuntat un recurs de tipus <Tag color="purple" style={{ margin: '0 4px' }}>{enunciatDiagram.type}</Tag> que es mostrarà a l'enunciat.
                                            </span>
                                        ) : (
                                            <span>Cap model interactiu vinculat a l'enunciat (opcional). Pots vincular un ER, Relacional o Script.</span>
                                        )}
                                    </div>
                                </Space>

                                <Space wrap>
                                    {enunciatDiagram ? (
                                        <>
                                            <Button
                                                size="small"
                                                type="primary"
                                                ghost
                                                icon={<EditOutlined />}
                                                onClick={() => {
                                                    setEnunciatDesignerType(enunciatDiagram.type);
                                                    setEnunciatDesignerVisible(true);
                                                }}
                                            >
                                                Editar {enunciatDiagram.type}
                                            </Button>
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => setEnunciatDiagram(null)}
                                            >
                                                Desvincular
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                size="small"
                                                icon={<PlusOutlined />}
                                                style={{ borderColor: '#722ed1', color: '#722ed1' }}
                                                onClick={() => {
                                                    setEnunciatDesignerType('ER');
                                                    setEnunciatDesignerVisible(true);
                                                }}
                                            >
                                                + Vincular ER
                                            </Button>
                                            <Button
                                                size="small"
                                                icon={<PlusOutlined />}
                                                style={{ borderColor: '#1890ff', color: '#1890ff' }}
                                                onClick={() => {
                                                    setEnunciatDesignerType('Relacional');
                                                    setEnunciatDesignerVisible(true);
                                                }}
                                            >
                                                + Vincular Model Relacional
                                            </Button>
                                            <Button
                                                size="small"
                                                icon={<PlusOutlined />}
                                                style={{ borderColor: '#13c2c2', color: '#13c2c2' }}
                                                onClick={() => {
                                                    setEnunciatDesignerType('Script');
                                                    setEnunciatDesignerVisible(true);
                                                }}
                                            >
                                                + Vincular Script
                                            </Button>
                                        </>
                                    )}
                                </Space>
                            </div>
                        </Card>
                    </div>

                    {/* Apartat de Pujada d'Imatges (Múltiples imatges amb zoom) */}
                    <Form.Item
                        label={
                            <Space size={6}>
                                <PictureOutlined style={{ color: '#52c41a' }} />
                                <span style={{ fontWeight: 600 }}>Imatges de suport / Diagrames de referència</span>
                                <Tooltip title="Pots adjuntar una o diverses imatges (esquemes, diagrames conceptuals o captures). Els alumnes les podran veure i clicar per ampliar-les a pantalla completa tant des de l'IDE com des de l'enunciat.">
                                    <QuestionCircleOutlined style={{ color: '#888' }} />
                                </Tooltip>
                            </Space>
                        }
                    >
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            onChange={handleUploadChange}
                            onPreview={handlePreview}
                            beforeUpload={() => false}
                            multiple
                            accept="image/*"
                        >
                            <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 6, fontSize: 12 }}>Afegir imatge</div>
                            </div>
                        </Upload>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                            Pots pujar múltiples fitxers (PNG, JPG, SVG). Clica damunt de qualsevol imatge per previsualitzar-la amb zoom.
                        </div>
                    </Form.Item>

                    <Form.Item
                        label={<span style={{ fontWeight: 600 }}>Descripció / Enunciat del problema</span>}
                        name="descripcio"
                        rules={[{ required: true, message: "Introdueix l'enunciat o context del problema" }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder={isEr
                                ? "Descriu l'escenari del món real, les entitats, els atributs i com es relacionen (cardinalitats 1:1, 1:N, N:N)..."
                                : "Escriu una descripció clara de la tasca que l'alumne ha de resoldre (admet format Markdown)..."}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Space>
                                    <CodeOutlined style={{ color: isDesignExercise ? '#722ed1' : '#1890ff' }} />
                                    <span style={{ fontWeight: 600 }}>
                                        {isEr ? "Solució Oficial del Model ER (JSON)" : (isScript ? "Solució Script Relacional" : (isRelacional ? "Solució Model Relacional (JSON)" : `Solució esperada / Query de Correcció (${selectedTipus})`))}
                                    </span>
                                    {isDesignExercise ? (
                                        <Tag color="purple">Model Interactiu</Tag>
                                    ) : (
                                        <Tag color={isNoSql ? 'green' : 'geekblue'}>{isNoSql ? 'NoSQL / JS' : 'Editor SQL'}</Tag>
                                    )}
                                </Space>

                                {isDesignExercise && (
                                    <Space size="small">
                                        <Tooltip title={`Dibuixa visualment la solució en l'editor interactiu de ${selectedTipus}`}>
                                            <Button
                                                size="small"
                                                type="primary"
                                                ghost
                                                icon={<FullscreenOutlined />}
                                                onClick={() => {
                                                    if (isEr) setErDesignerVisible(true);
                                                    else if (isScript) setScriptDesignerVisible(true);
                                                    else if (isRelacional) setRelationalDesignerVisible(true);
                                                }}
                                            >
                                                Obrir Editor Visual {selectedTipus}
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Exportar la solució actual a fitxer JSON">
                                            <Button
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={handleExportCorreccioJson}
                                            >
                                                Exportar JSON
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Carregar solució des d'un fitxer JSON extern">
                                            <Button
                                                size="small"
                                                icon={<UploadOutlined />}
                                                onClick={() => jsonFileInputRef.current?.click()}
                                            >
                                                Importar JSON
                                            </Button>
                                        </Tooltip>
                                        <input
                                            type="file"
                                            accept=".json,.txt"
                                            ref={jsonFileInputRef}
                                            style={{ display: 'none' }}
                                            onChange={handleImportCorreccioJson}
                                        />
                                    </Space>
                                )}
                            </div>
                        }
                        name="correccio"
                        tooltip={isEr
                            ? "Estructura JSON que defineix les entitats, relacions, atributs i connexions correctes del diagrama"
                            : (isScript
                                ? "Línies de taules i atributs del Script Relacional (o format JSON)"
                                : (isRelacional
                                    ? "Estructura JSON que defineix les taules, tipus de dades i claus foranes (FK)"
                                    : "La sentència SQL o comanda NoSQL esperada contra la qual es validarà la resposta"))}
                    >
                        {isDesignExercise ? (
                            <TextArea
                                rows={7}
                                placeholder={isEr
                                    ? '{\n  "type": "ER",\n  "entities": [{ "id": "e1", "name": "EQUIP", "isWeak": false }],\n  "relationships": [{ "id": "r1", "name": "JUGA", "cardinality": "1:N" }],\n  "attributes": [{ "id": "a1", "parentId": "e1", "name": "id", "isPK": true }]\n}'
                                    : (isScript
                                        ? 'EQUIPO (*nombre_eq*, dirección, telefono, presidente)\nESTADIO (*nombre_est*, dirección_est, capacidad, nombre_eq (EQUIPO))'
                                        : '{\n  "type": "Relacional",\n  "tables": [\n    { "name": "EQUIPO", "columns": [{ "name": "nombre_eq", "type": "VARCHAR(50)", "isPK": true }] }\n  ],\n  "foreignKeys": []\n}')}
                                style={{ fontFamily: 'monospace', fontSize: 13 }}
                            />
                        ) : (
                            <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                                <Editor
                                    height="200px"
                                    language={codeEditorLanguage}
                                    theme="vs-light"
                                    value={form.getFieldValue('correccio') || ''}
                                    onChange={(value) => {
                                        form.setFieldsValue({ correccio: value || '' });
                                    }}
                                    options={{
                                        minimap: { enabled: false },
                                        fontSize: 13,
                                        scrollBeyondLastLine: false,
                                        wordWrap: 'on',
                                        lineNumbers: 'on',
                                        folding: false,
                                        automaticLayout: true
                                    }}
                                />
                            </div>
                        )}
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal de Previsualització d'Imatge */}
            {previewOpen && (
                <Modal
                    open={previewOpen}
                    title="Vista prèvia de la imatge"
                    footer={null}
                    onCancel={() => setPreviewOpen(false)}
                >
                    <img alt="preview" style={{ width: '100%', borderRadius: 8 }} src={previewImage} />
                </Modal>
            )}

            {/* Modal per dissenyar el Model/Diagrama Vinculat a l'Enunciat */}
            {enunciatDesignerVisible && (
                <Modal
                    title={`Editor de Model per a l'Enunciat (${enunciatDesignerType})`}
                    open={enunciatDesignerVisible}
                    onCancel={() => setEnunciatDesignerVisible(false)}
                    width="92vw"
                    style={{ top: 20 }}
                    bodyStyle={{ height: '80vh', padding: 0 }}
                    footer={[
                        <Button key="close" type="primary" onClick={() => setEnunciatDesignerVisible(false)}>
                            Desar i Tancar
                        </Button>
                    ]}
                >
                    {enunciatDesignerType === 'ER' && (
                        <ErDiagramIde
                            initialData={enunciatDiagram?.type === 'ER' ? enunciatDiagram.data : null}
                            isTeacherMode={true}
                            title={`Diagrama ER per a l'Enunciat · ${form.getFieldValue('nom') || 'Exercici'}`}
                            onSave={(data: ErDiagramData) => {
                                setEnunciatDiagram({
                                    type: 'ER',
                                    data: data
                                });
                            }}
                        />
                    )}
                    {enunciatDesignerType === 'Relacional' && (
                        <RelationalDiagramIde
                            initialData={enunciatDiagram?.type === 'Relacional' ? enunciatDiagram.data : null}
                            isTeacherMode={true}
                            title={`Model Relacional per a l'Enunciat · ${form.getFieldValue('nom') || 'Exercici'}`}
                            onSave={(data) => {
                                setEnunciatDiagram({
                                    type: 'Relacional',
                                    data: data
                                });
                            }}
                        />
                    )}
                    {enunciatDesignerType === 'Script' && (
                        <ScriptRelationalIde
                            initialData={enunciatDiagram?.type === 'Script' ? enunciatDiagram.data : null}
                            isTeacherMode={true}
                            title={`Script Relacional per a l'Enunciat · ${form.getFieldValue('nom') || 'Exercici'}`}
                            onSave={(data, formattedText) => {
                                setEnunciatDiagram({
                                    type: 'Script',
                                    data: formattedText
                                });
                            }}
                        />
                    )}
                </Modal>
            )}

            {/* Modal per dissenyar la solució ER directament de forma visual */}
            {erDesignerVisible && (
                <Modal
                    title="Editor Visual de Solució ER"
                    open={erDesignerVisible}
                    onCancel={() => setErDesignerVisible(false)}
                    width="92vw"
                    style={{ top: 20 }}
                    bodyStyle={{ height: '80vh', padding: 0 }}
                    footer={[
                        <Button key="close" onClick={() => setErDesignerVisible(false)}>
                            Tancar
                        </Button>
                    ]}
                >
                    <ErDiagramIde
                        initialData={form.getFieldValue('correccio')}
                        isTeacherMode={true}
                        title={`Solució ER · ${form.getFieldValue('nom') || 'Exercici'}`}
                        onSave={(data: ErDiagramData) => {
                            form.setFieldsValue({
                                correccio: JSON.stringify(data, null, 2)
                            });
                        }}
                    />
                </Modal>
            )}

            {/* Modal per dissenyar la solució Script Relacional */}
            {scriptDesignerVisible && (
                <Modal
                    title="Editor Visual de Solució Script Relacional"
                    open={scriptDesignerVisible}
                    onCancel={() => setScriptDesignerVisible(false)}
                    width="92vw"
                    style={{ top: 20 }}
                    bodyStyle={{ height: '80vh', padding: 0 }}
                    footer={[
                        <Button key="close" onClick={() => setScriptDesignerVisible(false)}>
                            Tancar
                        </Button>
                    ]}
                >
                    <ScriptRelationalIde
                        initialData={form.getFieldValue('correccio')}
                        isTeacherMode={true}
                        title={`Solució Script Relacional · ${form.getFieldValue('nom') || 'Exercici'}`}
                        onSave={(data, formattedText) => {
                            form.setFieldsValue({
                                correccio: formattedText
                            });
                        }}
                    />
                </Modal>
            )}

            {/* Modal per dissenyar la solució Model Relacional de Taules */}
            {relationalDesignerVisible && (
                <Modal
                    title="Editor Visual de Solució Model Relacional (Taules)"
                    open={relationalDesignerVisible}
                    onCancel={() => setRelationalDesignerVisible(false)}
                    width="92vw"
                    style={{ top: 20 }}
                    bodyStyle={{ height: '80vh', padding: 0 }}
                    footer={[
                        <Button key="close" onClick={() => setRelationalDesignerVisible(false)}>
                            Tancar
                        </Button>
                    ]}
                >
                    <RelationalDiagramIde
                        initialData={form.getFieldValue('correccio')}
                        isTeacherMode={true}
                        title={`Solució Model Relacional · ${form.getFieldValue('nom') || 'Exercici'}`}
                        onSave={(data) => {
                            form.setFieldsValue({
                                correccio: JSON.stringify(data, null, 2)
                            });
                        }}
                    />
                </Modal>
            )}

            {/* Modal de Configuració Avançada de Tests i Rúbrica de 100 punts */}
            {configTestsVisible && (
                <ConfiguracioTestsDissenyModal
                    visible={configTestsVisible}
                    tipus={selectedTipus}
                    exerciseName={form.getFieldValue('nom') || 'Exercici'}
                    solutionRaw={form.getFieldValue('correccio') || ''}
                    initialChecksStr={form.getFieldValue('checks') || ''}
                    onClose={(savedConfig) => {
                        if (savedConfig) {
                            const prefix = selectedTipus === 'ER' ? 'ER-VALIDATION' : (selectedTipus === 'Script' ? 'SCRIPT-VALIDATION' : 'RELATIONAL-VALIDATION');
                            const jsonConfig = JSON.stringify(savedConfig);
                            const finalChecks = `${prefix}#${jsonConfig}`;
                            form.setFieldsValue({ checks: finalChecks });
                            if (savedConfig.penalitzacioPerIntent !== undefined) {
                                setPenalitzacioIntent(savedConfig.penalitzacioPerIntent);
                            }
                        }
                        setConfigTestsVisible(false);
                    }}
                />
            )}

            {/* Modal de Configuració de Tests i Comprovacions SQL */}
            {configSqlTestsVisible && (
                <ConfiguracioTestsSqlModal
                    visible={configSqlTestsVisible}
                    tipus={selectedTipus}
                    exerciseName={form.getFieldValue('nom') || 'Exercici'}
                    solutionRaw={form.getFieldValue('correccio') || ''}
                    initialChecksStr={form.getFieldValue('checks') || ''}
                    onClose={(savedChecksStr, savedConfig) => {
                        if (savedChecksStr !== null && savedChecksStr !== undefined) {
                            form.setFieldsValue({ checks: savedChecksStr });
                            if (savedConfig && savedConfig.penalitzacioPerIntent !== undefined) {
                                setPenalitzacioIntent(savedConfig.penalitzacioPerIntent);
                            }
                        }
                        setConfigSqlTestsVisible(false);
                    }}
                />
            )}
        </>
    );
};

export default NouExerciciBbddModal;

